import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, Row, Col, Badge } from 'antd';
import {
  ShoppingCartOutlined, InboxOutlined, BankOutlined, FileDoneOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import { formatMontant } from '../../variables/constants';
import { listerCommandes } from '../../services/attestationsService';

import CommandesTab from './CommandesTab';
import LotsTab from './LotsTab';
import ChequesTab from './ChequesTab';
import FacturesTab from './FacturesTab';
import NouvelleCommandeModal from './modals/NouvelleCommandeModal';
import NouveauLotModal from './modals/NouveauLotModal';

import { useTranslation } from 'react-i18next';

const ONGLETS = ['commandes', 'lots', 'cheques', 'factures'];

export default function Attestations() {
  const { t } = useTranslation('attestations');
  const navigate = useNavigate();
  // L'onglet actif est dans l'URL (?onglet=cheques) : revenir du détail d'une
  // commande ramène à l'onglet d'où on l'avait ouverte.
  const [searchParams, setSearchParams] = useSearchParams();
  const ongletUrl = searchParams.get('onglet');
  const activeTab = ONGLETS.includes(ongletUrl) ? ongletUrl : 'commandes';
  const setActiveTab = (cle) => setSearchParams(cle === 'commandes' ? {} : { onglet: cle }, { replace: true });
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState({ total: 0, attente: 0, livrees: 0, ca: 0 });

  const [modalCommande, setModalCommande] = useState(false);
  const [modalLot, setModalLot] = useState(false);

  const refreshAll = useCallback(() => setRefreshKey(k => k + 1), []);

  const loadStats = useCallback(async () => {
    try {
      const PAGE_SIZE = 200;
      const first = await listerCommandes(0, PAGE_SIZE);
      let all = [...(first.items || [])];
      const totalPages = first.totalPages || 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => listerCommandes(i + 1, PAGE_SIZE))
        );
        rest.forEach(r => { all = all.concat(r.items || []); });
      }
      setStats({
        total: first.totalElements || all.length,
        attente: all.filter(c => c.statut === 'PROFORMA_EMISE' || c.statut === 'CHEQUE_RECU').length,
        livrees: all.filter(c => c.statut === 'LIVRE' || c.statut === 'FACTURE_EMISE' || c.statut === 'SOLDE').length,
        ca: all.filter(c => c.statut !== 'ANNULEE').reduce((s, c) => s + (Number(c.montantTotal) || 0), 0),
      });
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats, refreshKey]);

  // Le détail d'une commande est une page, plus un tiroir latéral.
  const ouvrirCommande = (id) => { if (id) navigate(`/attestations/commandes/${id}`); };
  const onOpenDetail = (commande) => ouvrirCommande(commande?.commandeTrackingId);
  const onOpenDetailById = ouvrirCommande;

  const KPI = ({ bg, color, label, value }) => (
    <div style={{ background: bg, borderRadius: 12, padding: '16px 20px', height: '100%' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>{label}</div>
    </div>
  );

  /* `actif` EST PASSE A CHAQUE ONGLET pour que son bouton d'export flottant ne se
     rende que la. AntD garde montes les volets deja visites : sans cette prop, les
     quatre boutons se superposeraient au meme coin de l'ecran. */
  const tabItems = [
    {
      key: 'commandes',
      label: <span><ShoppingCartOutlined /> {t('page.onglets.commandes')} <Badge count={stats.total} style={{ backgroundColor: '#1a6b3c', marginLeft: 6 }} /></span>,
      children: (
        <CommandesTab
          actif={activeTab === 'commandes'}
          refreshKey={refreshKey}
          onOpenDetail={onOpenDetail}
          onCreate={() => setModalCommande(true)}
        />
      ),
    },
    {
      key: 'lots',
      label: <span><InboxOutlined /> {t('page.onglets.lots')}</span>,
      children: <LotsTab actif={activeTab === 'lots'} refreshKey={refreshKey} onCreate={() => setModalLot(true)} />,
    },
    {
      key: 'cheques',
      label: <span><BankOutlined /> {t('page.onglets.cheques')}</span>,
      children: <ChequesTab actif={activeTab === 'cheques'} refreshKey={refreshKey} onOpenCommande={onOpenDetailById} />,
    },
    {
      key: 'factures',
      label: <span><FileDoneOutlined /> {t('page.onglets.factures')}</span>,
      children: <FacturesTab actif={activeTab === 'factures'} refreshKey={refreshKey} />,
    },
  ];

  return (
    <Layout title={t('page.titreOnglet')}>
      <div className="cb-page-header">
        <div>
          <h1 className="cb-page-title">{t('page.titre')}</h1>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={6}><KPI bg="#e8f5ed" color="#1a6b3c" label={t('page.kpi.totalCommandes')} value={stats.total} /></Col>
        <Col xs={12} md={6}><KPI bg="#fef3c7" color="#92400e" label={t('page.kpi.enCours')} value={stats.attente} /></Col>
        <Col xs={12} md={6}><KPI bg="#dbeafe" color="#1e40af" label={t('page.kpi.livrees')} value={stats.livrees} /></Col>
        <Col xs={12} md={6}><KPI bg="#dcfce7" color="#166534" label={t('page.kpi.chiffreAffaires')} value={formatMontant(stats.ca)} /></Col>
      </Row>

      <div className="cb-card" style={{ padding: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} type="line"
          tabBarStyle={{ marginBottom: 16 }} />
      </div>

      <NouvelleCommandeModal
        open={modalCommande}
        onClose={() => setModalCommande(false)}
        onCreated={(cmd) => { refreshAll(); ouvrirCommande(cmd?.commandeTrackingId); }} />

      <NouveauLotModal
        open={modalLot}
        onClose={() => setModalLot(false)}
        onCreated={() => refreshAll()} />

    </Layout>
  );
}
